import { Controller } from "react-hook-form";
import { WEB_URL } from "@/lib/webUrl";
import { useQuery } from "@tanstack/react-query";
import { guideSchema, type GuideInput } from "@ckgo2/shared/schemas";
import type { Guide } from "@ckgo2/shared/types";
import { api } from "@/api/client";
import MediaPicker from "@/components/common/MediaPicker";
import { RichTextEditor } from "@/components/editor/RichTextEditor";
import EditPageHeader from "@/components/common/EditPageHeader";
import { useEditPage } from "@/hooks/useEditPage";

export default function GuideEditPage() {
  const { isNew, isLoading, form, saveMutation, onSubmit, itemData } = useEditPage<
    GuideInput,
    Guide
  >({
    resourceKey: "guide",
    listKey: "guides",
    apiPath: "/api/admin/guides",
    schema: guideSchema,
    defaultValues: {
      name: "",
      slug: "",
      photo: "",
      photoMediaId: null,
      bio: "",
      specializations: [],
      yearsExperience: 0,
      homeDestinationId: null,
      isActive: true,
      sortOrder: 0,
    },
    navigateTo: "/guides",
    slugSourceField: "name",
  });

  // Destination team this guide belongs to (drives the schedule's destination-first grouping).
  const { data: destData } = useQuery({
    queryKey: ["destinations", "lookup"],
    queryFn: () =>
      api.get<{ data: { id: string; name: string }[] }>("/api/admin/destinations?limit=200"),
  });
  const destinations = destData?.data ?? [];

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    control,
    formState: { errors, isSubmitting, isDirty },
  } = form;

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="border-primary-500 h-8 w-8 animate-spin rounded-full border-4 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <EditPageHeader
        backUrl="/guides"
        isNew={isNew}
        newTitle="New guide"
        editTitle="Edit guide"
        entityName={itemData?.name}
        viewUrl={!isNew && watch("slug") ? `${WEB_URL}/guide/${watch("slug")}` : undefined}
        formId="guide-form"
        isSubmitting={isSubmitting}
        isDirty={isDirty}
        error={saveMutation.error}
      />

      <form id="guide-form" onSubmit={handleSubmit(onSubmit)} className="grid gap-6 lg:grid-cols-3">
        {/* Main content - 2 columns */}
        <div className="space-y-6 lg:col-span-2">
          {/* Basic info */}
          <div className="card">
            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
              Basic information
            </h2>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="name" className="label">
                  Name *
                </label>
                <input
                  {...register("name")}
                  type="text"
                  id="name"
                  className={`input ${errors.name ? "input-error" : ""}`}
                  placeholder="e.g. John Smith"
                />
                {errors.name && (
                  <p className="text-error-600 mt-1 text-sm">{errors.name.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="slug" className="label">
                  URL slug *
                </label>
                <input
                  {...register("slug")}
                  type="text"
                  id="slug"
                  className={`input ${errors.slug ? "input-error" : ""}`}
                  placeholder="e.g. john-smith"
                />
                {errors.slug && (
                  <p className="text-error-600 mt-1 text-sm">{errors.slug.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="yearsExperience" className="label">
                  Years of experience
                </label>
                <input
                  {...register("yearsExperience", { valueAsNumber: true })}
                  type="number"
                  id="yearsExperience"
                  className="input"
                  min={0}
                />
              </div>

              <div>
                <label htmlFor="livingIn" className="label">
                  Location
                </label>
                <input
                  {...register("livingIn")}
                  type="text"
                  id="livingIn"
                  className="input"
                  placeholder="Ho Chi Minh City, Vietnam"
                />
              </div>
            </div>
          </div>

          {/* Bio */}
          <div className="card">
            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">Biography</h2>
            <div>
              <label className="label">Biography (EN)</label>
              <Controller
                name="bio"
                control={control}
                render={({ field }) => (
                  <RichTextEditor
                    content={field.value || ""}
                    onChange={field.onChange}
                    placeholder="Short guide biography..."
                  />
                )}
              />
            </div>
          </div>

          {/* Specializations */}
          <div className="card">
            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
              Specializations
            </h2>
            <div>
              <label htmlFor="specializations" className="label">
                Specializations
              </label>
              <textarea
                id="specializations"
                rows={3}
                className="input"
                placeholder="Asia, Trekking, Diving"
                value={watch("specializations")?.join(", ") ?? ""}
                onChange={(e) => {
                  const value = e.target.value
                    .split(",")
                    .map((s) => s.trim())
                    .filter(Boolean);
                  setValue("specializations", value);
                }}
              />
              <p className="mt-1 text-xs text-gray-500">Separate with commas</p>
            </div>
          </div>
        </div>

        {/* Sidebar - 1 column */}
        <div className="space-y-6">
          <div className="card">
            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">Photo</h2>
            <Controller
              name="photoMediaId"
              control={control}
              render={({ field }) => (
                <MediaPicker
                  mode="single"
                  folder="guides"
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          </div>

          <div className="card">
            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">Settings</h2>
            <div className="space-y-4">
              <div>
                <label htmlFor="homeDestinationId" className="label">
                  Home destination
                </label>
                <select id="homeDestinationId" {...register("homeDestinationId")} className="input">
                  <option value="">— none —</option>
                  {destinations.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  The destination team this guide belongs to — groups them in the schedule.
                </p>
              </div>

              <div>
                <label htmlFor="sortOrder" className="label">
                  Sort order
                </label>
                <input
                  {...register("sortOrder", { valueAsNumber: true })}
                  type="number"
                  id="sortOrder"
                  className="input"
                  min={0}
                />
                <p className="mt-1 text-xs text-gray-500">Lower number = higher priority</p>
              </div>

              <div className="flex items-center gap-3">
                <input
                  {...register("isActive")}
                  type="checkbox"
                  id="isActive"
                  className="text-primary-600 focus:ring-primary-500 h-4 w-4 rounded border-gray-300"
                />
                <label
                  htmlFor="isActive"
                  className="text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Active guide
                </label>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
